/* eslint-disable no-nested-ternary */
import { useMemo, useState, useCallback } from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../store/hooks';
import {
  ResetRewardsContentList,
  selectRewardsContentPageNumber,
  selectGetRewardsContentState,
  selectRewardsHasMoreContent,
  selectRewardsContentList,
  UpdateRewardsHasMoreContent,
  UpdateRewardsPageNumber,
} from '../../../store/slices/contentSlice';
import { getRewardsContent } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import {
  CAPITAL_WRAPS_NAME,
  HD_SUPPLY_NAME,
  SPARTAN_TOOL_NAME,
  WESUPPLYTRADES_NAME,
  WINSUPPLY_NAME,
} from '../../../common/constants';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useFetchCategoryContent } from './useFetchCategoryContent';
import { Content, ContentFeedSection } from '../../../common/models/content/Content';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';

interface RewardsContentCategoryProps {
  categoryId: string;
  keyProp: string;
  setSomeContentLoadedInitially: (value: boolean) => void;
  contentFeedSection: ContentFeedSection
  viewMorePage?: boolean
}

export function RewardsContentCategory({
  categoryId,
  keyProp,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}:RewardsContentCategoryProps) {
  const storeRewardsContentList = useAppSelector(selectRewardsContentList);
  const rewardsContentList = useMemo(() => storeRewardsContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storeRewardsContentList, contentFeedSection]);

  const getRewardsContentState = useAppSelector(selectGetRewardsContentState);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { t } = useTranslation();
  const rewardsPage = useAppSelector(selectRewardsContentPageNumber);
  const rewardsHasMore = useAppSelector(selectRewardsHasMoreContent);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useTrackCategoryContentVisibility({
    categoryContentList: rewardsContentList,
  });

  const fetchMoreCategoryContent = useFetchCategoryContent({
    viewMorePage,
    contentRequestOptions: {
      rewardPartners: `Home Depot,${HD_SUPPLY_NAME},${WINSUPPLY_NAME},${CAPITAL_WRAPS_NAME},${SPARTAN_TOOL_NAME},${WESUPPLYTRADES_NAME}`,
    },
    getCategoryContent: getRewardsContent,
    resetCategoryContentList: ResetRewardsContentList,
    setSomeContentLoadedInitially,
    setOpenErrorSnackbar,
    setInitialFetchDone,
    updateCategoryPageNumber: UpdateRewardsPageNumber,
    updateHasMoreCategoryContent: UpdateRewardsHasMoreContent,
  });

  const isLoading = useMemo(
    () => (getRewardsContentState === 'pending' && !initialFetchDone),
    [getRewardsContentState, initialFetchDone],
  );

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  const onNextPage = useCallback(() => {
    fetchMoreCategoryContent(
      rewardsPage,
      rewardsHasMore,
    );
  }, [rewardsPage, rewardsHasMore, fetchMoreCategoryContent]);

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      {isLoading ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${keyProp}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${keyProp}-content`}
          categoryId={categoryId}
          storeContentList={rewardsContentList}
          onNextPage={onNextPage}
          hasMoreContent={rewardsHasMore}
          showSearchBar={true}
        />
      ) : rewardsContentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${keyProp}-content`}
          categoryTitle="Rewards and more"
          storeContentList={rewardsContentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
